/**
 * MER-208: pure batching state for dashboard usage telemetry. Plain
 * .mjs so the node:test suite imports it directly (the
 * fallbackWindow.mjs precedent). No timers and no I/O in here: the
 * queue answers "what should flush, and when is the next flush due"
 * against an injected clock; telemetry.ts owns setTimeout and the
 * Supabase insert.
 *
 * Contract (acceptance 9): the FIRST event arms a single flush window
 * of flushIntervalMs; everything enqueued inside the window joins one
 * batch. Ten rapid clicks produce one batch, not ten.
 */

export const DEFAULT_FLUSH_INTERVAL_MS = 5000;

/**
 * @param {{ flushIntervalMs?: number, maxQueued?: number }} [opts]
 *   maxQueued caps unbounded growth if flushing is impossible (e.g.
 *   telemetry disabled mid-session); oldest events drop first.
 */
export function createTelemetryQueue(opts = {}) {
  const flushIntervalMs = opts.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  const maxQueued = opts.maxQueued ?? 200;
  let queue = [];
  let dueAt = null;

  return {
    /**
     * @param {{ event_type: string, payload: object, occurred_at: string }} event
     * @param {number} now   epoch ms
     * @returns {number | null}   when a flush became newly due (epoch
     *   ms) if this enqueue armed the window, else null. The caller
     *   uses a non-null return to schedule its one timer.
     */
    enqueue(event, now) {
      queue.push(event);
      if (queue.length > maxQueued) queue = queue.slice(queue.length - maxQueued);
      if (dueAt === null) {
        dueAt = now + flushIntervalMs;
        return dueAt;
      }
      return null;
    },

    /**
     * Drain the batch if the window has elapsed. Returns the batch
     * (and disarms) or null when nothing is due yet.
     * @param {number} now   epoch ms
     */
    drainIfDue(now) {
      if (dueAt === null || now < dueAt || queue.length === 0) return null;
      const batch = queue;
      queue = [];
      dueAt = null;
      return batch;
    },

    /** Unconditional drain (sign-out path). Empty array when idle. */
    drainAll() {
      const batch = queue;
      queue = [];
      dueAt = null;
      return batch;
    },

    size() {
      return queue.length;
    },

    nextDueAt() {
      return dueAt;
    },
  };
}
